import ExclamationIcon from 'components/ReusableComponents/Icons/Exclamation'
import TextInput from 'components/ReusableComponents/ManageHotel/TextInput'
import { useState } from 'react'
import { useToasts } from 'react-toast-notifications'
import subsidiariesApi from 'services/api/subsidiaries'
import { LandingPageDomainPreference } from 'utils/enums'

import ConfirmationDialog from '../../../components/ReusableComponents/ConfirmationDialog/index'
import CNAMEInstructions from './CNAMEInstructions'
import styles from './EditSubDomainControlDialog.module.scss'
import validateDomain from './validateDomain'

const EditSubDomainControlDialog = ({
  subdomain: subdomainFromProps,
  domain: domainFromProps,
  landingPageDomainPreference: landingPageDomainPreferenceFromProps,
  defaultChainSubdomain,
  handleClose,
  handleConfirm
}) => {
  const { addToast } = useToasts()

  const [subdomain, setSubdomain] = useState(subdomainFromProps)
  const [domain, setDomain] = useState(domainFromProps)
  const [landingPageDomainPreference, setLandingPageDomainPreference] = useState(
    landingPageDomainPreferenceFromProps
  )
  const [customSubdomainPlusDomain, setCustomSubdomainPlusDomain] = useState(
    landingPageDomainPreferenceFromProps === LandingPageDomainPreference.Domain
      ? `${subdomain}.${domain}`
      : ''
  )

  const [showCustomSubdomainModal, setShowCustomSubdomainModal] = useState(false)
  const [showCustomDomainModal, setShowCustomDomainModal] = useState(false)
  const [showLearnMoreModal, setShowLearnMoreModal] = useState(false)

  const isDefaultDomainPreference =
    landingPageDomainPreference === LandingPageDomainPreference.Default

  const handleCustomDomainConfirm = async () => {
    try {
      // Check if the customSubdomainPlusDomain is following the format a.b.c
      // where a is the subdomain and b.c is the domain

      if (!customSubdomainPlusDomain.includes('.')) {
        addToast('Please enter a valid domain', {
          appearance: 'error',
          autoDismiss: true
        })
        return
      } else if (customSubdomainPlusDomain.split('.').length !== 3) {
        addToast('Please enter a valid domain', {
          appearance: 'error',
          autoDismiss: true
        })
        return
      }

      const [subdomain, domain, tld] = customSubdomainPlusDomain.split('.')

      const validDomainVal = validateDomain(domain)
      const validSubdomainVal = validateDomain(subdomain)

      if (!validDomainVal.isValid) {
        addToast(validDomainVal.message, {
          appearance: 'error',
          autoDismiss: true
        })
        return
      }

      if (!validSubdomainVal.isValid) {
        addToast(validSubdomainVal.message, {
          appearance: 'error',
          autoDismiss: true
        })
        return
      }

      if (!tld) {
        addToast('Please enter a valid url extension', {
          appearance: 'error',
          autoDismiss: true
        })
        return
      }

      const result = await subsidiariesApi.domain.checkDnsRecord({
        domain: customSubdomainPlusDomain
      })

      if (!result) {
        addToast('DNS propagation is not completed, please try again later', {
          appearance: 'error',
          autoDismiss: true
        })
        return
      }

      handleConfirm({
        subdomain,
        domain: `${domain}.${tld}`,
        landingPageDomainPreference
      })
    } catch (error) {
      addToast(error.message, {
        appearance: 'error',
        autoDismiss: true
      })
    }
  }

  const handleSubdomainConfirm = () => {
    const isValidSubdomain = validateDomain(subdomain)

    if (!isValidSubdomain.isValid) {
      addToast(isValidSubdomain.message, {
        appearance: 'error',
        autoDismiss: true
      })
      return
    }

    handleConfirm({
      subdomain,
      domain,
      landingPageDomainPreference
    })
  }

  const handleEdit = () => {
    if (landingPageDomainPreference === LandingPageDomainPreference.Subdomain) {
      setShowCustomSubdomainModal(true)
    } else if (landingPageDomainPreference === LandingPageDomainPreference.Domain) {
      setShowCustomDomainModal(true)
    }
  }

  return (
    <ConfirmationDialog
      title="Guest landing page URL"
      theme="blacklist"
      positiveMessage="Save changes"
      negativeMessage={
        isDefaultDomainPreference ? null : `Edit ${landingPageDomainPreference}`
      }
      onClose={handleClose}
      onReject={handleEdit}
      onConfirm={() =>
        handleConfirm({
          subdomain,
          domain,
          landingPageDomainPreference
        })
      }
    >
      <div className={styles.container}>
        <div className={styles.customSubContainer}>
          <label
            className={
              landingPageDomainPreference === LandingPageDomainPreference.Default
                ? styles.barBlue
                : styles.bar
            }
          >
            <div>
              <input
                type="radio"
                name="subDomainOption"
                checked={
                  landingPageDomainPreference === LandingPageDomainPreference.Default
                }
                onChange={() =>
                  setLandingPageDomainPreference(LandingPageDomainPreference.Default)
                }
              />
              <span>Use brand URL</span>
            </div>
            <span className={styles.domainOption}>
              {defaultChainSubdomain}.directful.com
            </span>
          </label>

          <label
            className={
              landingPageDomainPreference === LandingPageDomainPreference.Subdomain
                ? styles.barBlue
                : styles.bar
            }
          >
            <div>
              <input
                type="radio"
                name="subDomainOption"
                checked={
                  landingPageDomainPreference === LandingPageDomainPreference.Subdomain
                }
                onChange={() => {
                  setLandingPageDomainPreference(LandingPageDomainPreference.Subdomain)
                  setSubdomain('')
                  setShowCustomSubdomainModal(true)
                }}
              />
              <span>Use a custom subdomain</span>
            </div>
            <span className={styles.domainOption}>
              {subdomain || 'custom'}.directful.com
            </span>
          </label>

          <label
            className={
              landingPageDomainPreference === LandingPageDomainPreference.Domain
                ? styles.barBlue
                : styles.bar
            }
          >
            <div>
              <input
                type="radio"
                name="subDomainOption"
                checked={
                  landingPageDomainPreference === LandingPageDomainPreference.Domain
                }
                onChange={() => {
                  setLandingPageDomainPreference(LandingPageDomainPreference.Domain)
                  setSubdomain('')
                  setDomain('')
                  setShowCustomDomainModal(true)
                }}
              />
              <span>Use your own domain</span>
            </div>
            <span className={styles.domainOption}>
              {customSubdomainPlusDomain || 'brand.yourdomain'}.com
            </span>
          </label>

          {showCustomSubdomainModal && (
            <ConfirmationDialog
              title="Use a custom subdomain"
              theme="blacklist"
              positiveMessage="Save"
              onBack={() => {
                setShowCustomSubdomainModal(false)
                setSubdomain(subdomainFromProps)
                setDomain(domainFromProps)
                setLandingPageDomainPreference(landingPageDomainPreferenceFromProps)
              }}
              onConfirm={handleSubdomainConfirm}
              hasCloseButton={false}
            >
              <div className={styles.container}>
                <div className={styles.customInput}>
                  <TextInput
                    label="Enter a custom subdomain"
                    value={subdomain || ''}
                    placeholder="yourbrand"
                    onChange={e => setSubdomain(e.target.value)}
                    className={styles.inputText}
                  />
                  <span className={styles.helperText}>.directful.com</span>
                </div>
              </div>
            </ConfirmationDialog>
          )}

          {showCustomDomainModal && (
            <ConfirmationDialog
              title="Use your own domain"
              theme="blacklist"
              positiveMessage="Test & Save"
              contentPadding="0"
              onBack={() => {
                setShowCustomDomainModal(false)
                setSubdomain(subdomainFromProps)
                setDomain(domainFromProps)
                setLandingPageDomainPreference(landingPageDomainPreferenceFromProps)
              }}
              onConfirm={handleCustomDomainConfirm}
              hasCloseButton={false}
            >
              <div className={styles.container}>
                <div className={styles.helperContainer}>
                  <div className={styles.helperIndicator}>
                    <ExclamationIcon />
                    <span>
                      To use your own domain, enter add "custombrand.directful.com" as a
                      CNAME record in your domain configuration.
                      <span
                        className={styles.learnMore}
                        onClick={() => setShowLearnMoreModal(true)}
                      >
                        Learn more
                      </span>
                    </span>
                  </div>
                </div>
                <div className={styles.customHelperInput}>
                  <TextInput
                    label="Your domain"
                    value={customSubdomainPlusDomain || ''}
                    placeholder="brand.yourdomain.com"
                    onChange={e => {
                      setCustomSubdomainPlusDomain(e.target.value)
                    }}
                    className={styles.inputText}
                  />
                </div>
              </div>
            </ConfirmationDialog>
          )}

          {showLearnMoreModal && (
            <ConfirmationDialog
              title="How to add a CNAME record"
              theme="blacklist"
              onClose={() => {
                setShowLearnMoreModal(false)
              }}
              negativeMessage="OK"
              hasConfirmButton={false}
              maxWidth={800}
            >
              <div className={styles.learnMoreContainer}>
                <CNAMEInstructions />
              </div>
            </ConfirmationDialog>
          )}
        </div>
      </div>
    </ConfirmationDialog>
  )
}

export default EditSubDomainControlDialog
